'use client';

// Storefront Button — design system component.
// 6 variants × 8 colors × 4 sizes × 2 shapes + icon slots + all states.

import { SpinnerGap } from '@phosphor-icons/react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

/**
 * Visual variants for buttons + icon buttons.
 *
 * - `gradient`: filled with a vertical gradient of the color, contrast text
 * - `solid`:    filled with the color, contrast text
 * - `soft`:     tinted background (color/10), color-toned text
 * - `outline`:  transparent bg, bordered, color-toned text
 * - `text`:     **transparent bg**, color-toned text/icon, soft tint on hover
 *               (use this for icon-only triggers that should disappear into chrome)
 * - `noise`:    same as solid; reserved for noise-textured fills
 */
export type ButtonVariant = 'gradient' | 'solid' | 'soft' | 'outline' | 'text' | 'noise';
export type ButtonColor = 'default' | 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';
export type ButtonShape = 'default' | 'rounded';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  shape?: ButtonShape;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  /**
   * Work is in flight: swaps the leading icon for a spinner, marks the button
   * `aria-busy`, and disables it so the action cannot be fired twice.
   */
  loading?: boolean;
};

// ── Size classes ──

const SIZE: Record<ButtonSize, string> = {
  xs: 'h-7 px-2.5 text-[11px] gap-1',
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-[var(--field-height)] px-6 text-base gap-2',
  lg: 'h-[var(--field-height-lg)] px-8 text-base gap-2.5',
};

const ICON_SIZE: Record<ButtonSize, string> = {
  xs: '[&_svg]:h-3 [&_svg]:w-3',
  sm: '[&_svg]:h-3.5 [&_svg]:w-3.5',
  md: '[&_svg]:h-4 [&_svg]:w-4',
  lg: '[&_svg]:h-5 [&_svg]:w-5',
};

// `xs` (28px) and `sm` (32px) are drawn below Apple's 44pt minimum on
// purpose — they are chips and utility buttons whose visual weight matters.
// A transparent pseudo-element widens the *hit area* to the floor instead,
// leaving the drawn size untouched. Centring via left-1/2 + -translate-x-1/2
// is geometric, so it behaves identically in RTL.
const TAP_TARGET =
  "relative before:absolute before:left-1/2 before:top-1/2 before:h-11 before:w-full before:min-w-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']";

// md/lg already clear the floor — they read --field-height (48px).
const TAP_TARGET_BY_SIZE: Record<ButtonSize, string> = {
  xs: TAP_TARGET,
  sm: TAP_TARGET,
  md: '',
  lg: '',
};

// ── Shape classes ──

const SHAPE: Record<ButtonShape, string> = {
  default: 'rounded-[var(--radius-field)]',
  rounded: 'rounded-full',
};

// ── Color palette → variant mapping ──
// Each color provides the raw values; variants compose them differently.

type ColorTokens = {
  bg: string;
  bgHover: string;
  text: string;
  border: string;
  soft: string;
  softText: string;
  softHover: string;
  gradient: string;
};

const COLORS: Record<ButtonColor, ColorTokens> = {
  default: {
    bg: 'bg-foreground',
    bgHover: 'hover:bg-foreground/90',
    text: 'text-background',
    border: 'border-foreground/20',
    soft: 'bg-foreground/10',
    softText: 'text-foreground',
    softHover: 'hover:bg-foreground/15',
    gradient: 'bg-gradient-to-b from-foreground/80 to-foreground',
  },
  primary: {
    bg: 'bg-primary',
    bgHover: 'hover:bg-primary/90',
    text: 'text-primary-foreground',
    border: 'border-primary/30',
    soft: 'bg-primary/10',
    softText: 'text-primary',
    softHover: 'hover:bg-primary/20',
    gradient: 'bg-gradient-to-b from-primary/80 to-primary',
  },
  secondary: {
    bg: 'bg-secondary',
    bgHover: 'hover:bg-secondary/80',
    text: 'text-secondary-foreground',
    border: 'border-secondary-foreground/20',
    soft: 'bg-secondary',
    softText: 'text-secondary-foreground',
    softHover: 'hover:bg-secondary/80',
    gradient: 'bg-gradient-to-b from-muted/60 to-muted/80',
  },
  accent: {
    bg: 'bg-accent',
    bgHover: 'hover:bg-accent/90',
    text: 'text-accent-foreground',
    border: 'border-accent/30',
    soft: 'bg-accent/10',
    softText: 'text-accent',
    softHover: 'hover:bg-accent/20',
    gradient: 'bg-gradient-to-b from-accent/80 to-accent',
  },
  info: {
    bg: 'bg-info',
    bgHover: 'hover:bg-info/90',
    text: 'text-info-foreground',
    border: 'border-info/30',
    soft: 'bg-info/10',
    softText: 'text-info',
    softHover: 'hover:bg-info/20',
    gradient: 'bg-gradient-to-b from-info/80 to-info',
  },
  success: {
    bg: 'bg-success',
    bgHover: 'hover:bg-success/90',
    text: 'text-success-foreground',
    border: 'border-success/30',
    soft: 'bg-success/10',
    softText: 'text-success',
    softHover: 'hover:bg-success/20',
    gradient: 'bg-gradient-to-b from-success/80 to-success',
  },
  warning: {
    bg: 'bg-warning',
    bgHover: 'hover:bg-warning/90',
    text: 'text-warning-foreground',
    border: 'border-warning/30',
    soft: 'bg-warning/10',
    softText: 'text-warning',
    softHover: 'hover:bg-warning/20',
    gradient: 'bg-gradient-to-b from-warning/80 to-warning',
  },
  error: {
    bg: 'bg-danger',
    bgHover: 'hover:bg-danger/90',
    text: 'text-danger-foreground',
    border: 'border-danger/30',
    soft: 'bg-danger/10',
    softText: 'text-danger',
    softHover: 'hover:bg-danger/20',
    gradient: 'bg-gradient-to-b from-danger/80 to-danger',
  },
};

// ── Variant × Color → class string ──

function variantClasses(variant: ButtonVariant, color: ButtonColor): string {
  const c = COLORS[color];

  switch (variant) {
    case 'gradient':
      return `${c.gradient} ${c.text} ${c.bgHover} border-none shadow-sm`;
    case 'solid':
      return `${c.bg} ${c.text} ${c.bgHover} border-none shadow-sm`;
    case 'soft':
      return `${c.soft} ${c.softText} ${c.softHover} border-none`;
    case 'outline':
      // One hover background only. This used to also emit
      // `hover:${c.soft.replace('bg-','bg-')}` — a no-op replace — so two
      // conflicting hover fills landed on the same element and CSS source
      // order picked the winner. IconButton emitted just softHover, so the
      // same variant hovered differently in the two components.
      return `bg-transparent ${c.softText} border ${c.border} ${c.softHover}`;
    case 'text':
      return `bg-transparent ${c.softText} border-none ${c.softHover}`;
    case 'noise':
      return `${c.bg} ${c.text} ${c.bgHover} border-none shadow-sm bg-[length:200px_200px] bg-[url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.08'/%3E%3C/svg%3E")]`;
  }
}

// ── Base classes shared by all buttons ──

const BASE = 'inline-flex items-center justify-center font-medium whitespace-nowrap select-none cursor-pointer transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';

/**
 * The class string Button renders with, exposed so a non-<button> element
 * (e.g. a CTA that must be a single `<a>`, not a button nested in a link)
 * can carry the same visual treatment without duplicating the variant/color
 * class logic above.
 */
export function buttonClassName({
  variant = 'solid',
  color = 'primary',
  size = 'md',
  shape = 'default',
  fullWidth,
  className,
}: Pick<ButtonProps, 'variant' | 'color' | 'size' | 'shape' | 'fullWidth' | 'className'> = {}): string {
  return [
    BASE,
    SIZE[size],
    ICON_SIZE[size],
    TAP_TARGET_BY_SIZE[size],
    SHAPE[shape],
    variantClasses(variant, color),
    fullWidth ? 'w-full' : '',
    className ?? '',
  ].filter(Boolean).join(' ');
}

// ── Component ──

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'solid',
    color = 'primary',
    size = 'md',
    shape = 'default',
    leftIcon,
    rightIcon,
    fullWidth,
    loading = false,
    className,
    children,
    type,
    disabled,
    ...rest
  },
  ref,
) {
  const cls = buttonClassName({ variant, color, size, shape, fullWidth, className });

  return (
    <button
      ref={ref}
      // React leaves <button> at the HTML default of type="submit", so one
      // dropped into a <form> submits it by accident. Opt in explicitly.
      type={type ?? 'button'}
      className={cls}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {/* The spinner takes the leading slot rather than sitting beside the
          icon, so the button's width does not jump when work starts. */}
      {loading ? (
        <span className="shrink-0">
          <SpinnerGap className="animate-spin" aria-hidden="true" />
        </span>
      ) : (
        leftIcon && <span className="shrink-0">{leftIcon}</span>
      )}
      {children}
      {rightIcon && <span className="shrink-0">{rightIcon}</span>}
    </button>
  );
});
